import { useEffect } from "react";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import CommonTextEditor from "components/common/CommonTextEditor";
import FormDatePicker from "components/form/FormDatePicker";
import FormTextInput from "components/form/FormTextInput";
import { useProjectFormContext } from "pages/project/context/ProjectFormContext";

export function newNewsUpdateItem() {
  return {
    id: `news-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    updateTitle: "",
    date: "",
    updateDescription: "",
  };
}

function newsItemFieldErrors(errors, item, index) {
  const byId = errors.newsUpdateErrorsById?.[item.id];
  if (byId) return byId;
  const byIndex = Array.isArray(errors.newsUpdateErrors) ? errors.newsUpdateErrors[index] : null;
  return byIndex || {};
}

export default function StepNewsUpdates() {
  const { values, setField, errors } = useProjectFormContext();

  const items = Array.isArray(values.newsUpdates) && values.newsUpdates.length
    ? values.newsUpdates
    : [newNewsUpdateItem()];

  useEffect(() => {
    if (!Array.isArray(values.newsUpdates) || values.newsUpdates.length === 0) {
      setField("newsUpdates", [newNewsUpdateItem()]);
    }
  }, [values.newsUpdates, setField]);

  const updateItem = (id, patch) => {
    setField(
      "newsUpdates",
      items.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    );
  };

  const addItem = () => {
    setField("newsUpdates", [...items, newNewsUpdateItem()]);
  };

  const removeItem = (id) => {
    if (items.length <= 1) return;
    setField(
      "newsUpdates",
      items.filter((item) => item.id !== id),
    );
  };

  return (
    <div className="space-y-5 transition-opacity duration-300">
      {errors.newsUpdates && (
        <p className="text-xs font-medium text-red-600" role="alert">
          {errors.newsUpdates}
        </p>
      )}

      {items.map((item, index) => {
        const itemErrors = newsItemFieldErrors(errors, item, index);
        return (
          <div
            key={item.id}
            className="rounded-xl border border-slate-200 bg-slate-50/50 p-5 shadow-sm space-y-4"
          >
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-text-700">
                Update {index + 1}
                {item.updateTitle ? (
                  <span className="ml-2 font-normal text-text-400">— {item.updateTitle}</span>
                ) : null}
              </h4>
              {items.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeItem(item.id)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-600 transition hover:bg-red-50"
                >
                  <FiTrash2 className="h-3.5 w-3.5" />
                  Remove
                </button>
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <FormTextInput
                label="Title"
                name={`news-title-${item.id}`}
                value={item.updateTitle}
                onChange={(e) => updateItem(item.id, { updateTitle: e.target.value })}
                placeholder="e.g. Q1 funding milestone reached"
                error={itemErrors.updateTitle}
              />
              <FormDatePicker
                label="Date"
                name={`news-date-${item.id}`}
                value={item.date}
                onChange={(e) => updateItem(item.id, { date: e.target.value })}
                showShortcuts={false}
                error={itemErrors.date}
              />
            </div>

            <fieldset className="min-w-0 border-0 p-0">
              <legend className="mb-2 block w-full px-0 text-xs font-semibold text-text-500">
                Description
              </legend>
              <div
                className={[
                  "overflow-hidden rounded-xl border bg-white",
                  itemErrors.updateDescription
                    ? "border-red-400 ring-1 ring-red-200"
                    : "border-slate-200 shadow-card",
                ].join(" ")}
              >
                <CommonTextEditor
                  value={item.updateDescription}
                  onChange={(html) => updateItem(item.id, { updateDescription: html })}
                  placeholder="Share the update details…"
                  height={220}
                  error={itemErrors.updateDescription}
                />
              </div>
              {itemErrors.updateDescription ? (
                <p className="mt-1 text-xs font-medium text-red-600" role="alert">
                  {itemErrors.updateDescription}
                </p>
              ) : null}
            </fieldset>
          </div>
        );
      })}

      <button
        type="button"
        onClick={addItem}
        className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-brand-400 bg-white py-3 text-sm font-semibold text-brand-600 transition hover:bg-brand-50"
      >
        <FiPlus className="h-4 w-4" />
        Add update
      </button>
    </div>
  );
}
